import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MeetingCalendarPage() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {}); }, []);

  const byDate = useMemo(() => {
    const map = {};
    meetings.forEach((m) => {
      const iso = m.scheduled_at || m.created_at;
      const day = iso.slice(0, 10);
      (map[day] = map[day] || []).push(m);
    });
    return map;
  }, [meetings]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay.getDay();
  const todayStr = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="meeting-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold text-white flex items-center gap-2"><CalendarDays className="w-6 h-6 text-cyan-400" /> Calendar</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev-month-btn" className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-white font-semibold w-32 text-center">{firstDay.toLocaleString(undefined, { month: "long", year: "numeric" })}</span>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next-month-btn" className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map((d) => <div key={d} className="text-[10px] font-bold uppercase text-slate-500 text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const dayMeetings = byDate[dateStr] || [];
            const isToday = dateStr === todayStr;
            return (
              <div key={i} className={`min-h-[72px] rounded-lg p-1.5 border ${isToday ? "border-cyan-400/40 bg-cyan-400/5" : "border-white/5"}`} data-testid={`calendar-day-${dateStr}`}>
                <span className={`text-xs ${isToday ? "text-cyan-300 font-bold" : "text-slate-400"}`}>{d}</span>
                <div className="space-y-0.5 mt-1">
                  {dayMeetings.slice(0, 2).map((m) => (
                    <button key={m.id} onClick={() => navigate(`/meeting/${m.id}`)} data-testid={`calendar-meeting-${m.id}`}
                      className={`block w-full text-[10px] truncate text-left px-1 py-0.5 rounded ${m.meeting_type === "class" ? "bg-fuchsia-500/20 text-fuchsia-300" : "bg-cyan-500/20 text-cyan-300"}`}>
                      {m.title}
                    </button>
                  ))}
                  {dayMeetings.length > 2 && <span className="text-[10px] text-slate-500">+{dayMeetings.length - 2} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
